/**
 * Salaried Personal Loan Insert Helper Functions
 * Migrated from 3in1cms/bajajfinserv-web/ui.solid/src/lib/connectionManager/helper.ts
 */

import type {
  ISalplInsertApiRequest,
  ISalPLAdditionalPayload,
  IAppDtls,
} from "../types/salplInsertApiTypes";
import { getCookie, getParams, getUtmParamsFromCookie } from "@repo/shared-utils/common-utils";
import { isOpenInApp } from "@repo/shared-utils/pwa-utils";
import { getOrDefaultValue } from "@repo/shared-utils/insert-params";
import { cryptoAPICalledData, decryptAESCBC256 } from "@repo/shared-utils/crypto";
import { logPageActionToNewRelic } from "@repo/shared-utils/newRelic-log-utils";
import { V5 } from "./constants";

/**
 * Gets M1 tracking parameters from URL
 * Returns tracking parameters if utm_source is "m1"
 * Original implementation from: lib/api/plInsertApi.ts
 */
export const getM1TrackingParams = () => {
  if (typeof window === "undefined") {
    return {};
  }

  const urlParams = new URLSearchParams(window.location.search);
  const utmSource = urlParams.get("utm_source");

  if (utmSource?.toLowerCase() === "m1") {
    return {
      sourceSystem: "M1",
      srcChannel: "M1",
    };
  }

  return {};
};

/**
 * Converts string to sentence case
 * Original implementation from: lib/connectionManager/helper.ts
 * @param data - String to convert
 * @returns Sentence case string
 */
const convertToSentenceCase = (data: string) => {
  return data ? data.charAt(0).toUpperCase() + data.slice(1).toLowerCase() : "";
};

/**
 * Gets app details array with defaults
 * Original implementation from: lib/connectionManager/helper.ts
 * @param appDetails - App details to process
 * @returns Processed app details with defaults
 */
const getAppDetails = (appDetails: IAppDtls[]) => {
  if (appDetails && appDetails.length > 0) {
    return appDetails.map((item: IAppDtls) => ({
      pan: item.pan ?? "",
      dob: item.dob ?? "",
      employmentType: item.employmentType ?? "",
      fullName: item.fullName ?? "",
      gender: item.gender ? convertToSentenceCase(item.gender) : "",
      kycDtls: {
        address_pin: item.kycDtls?.address_pin ?? "",
        address_city: item.kycDtls?.address_city ?? "",
      },
      gstInNo: item.gstInNo ?? "",
      monthlyIncome: item.monthlyIncome,
    }));
  }
  return [
    {
      pan: "",
      dob: "",
      employmentType: "",
      fullName: "",
      gender: "",
      kycDtls: {
        address_pin: "",
        address_city: "",
      },
      gstInNo: "",
      monthlyIncome: undefined,
    },
  ];
};

/**
 * Gets SAL PL specific additional payload
 * Original implementation from: lib/connectionManager/helper.ts getSalPLadditionalPayload
 *
 * @param reqData - Request data
 * @param journeyName - Journey name (e.g., "SAL_NO_OFFER")
 * @returns Additional payload to merge with request
 */
export const getSalPLadditionalPayload = (
  reqData: ISalPLAdditionalPayload,
  journeyName: string
): Record<string, any> => {
  const utmParams = getUtmParamsFromCookie() as
    | {
        utmSource?: string;
        utmContent?: string;
        utmCampaign?: string;
        utmMedium?: string;
        utmTerm?: string;
      }
    | undefined;

  return {
    id: reqData?.id || "",
    journeyName: reqData?.journeyName || journeyName,
    eventCode: "B2C_PDP",
    mcpFlag: "N",
    sourcingBranchId: reqData?.sourcingBranchId || "320",
    revisedOfferAmt: "",
    offerId: reqData?.offerId || "",
    tncFlag: "",
    srcChannelCategory: "3in1",
    pqOfferRoiAnnual: "14",
    offerProduct: reqData?.offerProduct || "PL",
    pwaVersion: "",
    nativeVersion: reqData?.nativeVersion || "",
    requestSource: isOpenInApp() ? "3in1 App" : "3in1 Web",
    srcChannel: reqData?.srcChannel || (isOpenInApp() ? "3in1 App" : "3in1 Web"),
    appDtls:
      reqData?.appDtls && reqData.appDtls.length > 0
        ? getAppDetails(reqData.appDtls)
        : getAppDetails([]),
    processType: reqData.processType ?? "",
    processSegmentation: reqData.processSegmentation ?? "",
    journeyType: reqData.journeyType ?? "",
    loanType: reqData.loanType ?? "",
    appVersion: reqData.appVersion ?? V5,
    sourceSystem: "3in1",
    cta: reqData.cta ?? "Continue",
    sourceUrl: typeof window !== "undefined" ? window.location.href : "",
    offerName: reqData?.offerName ?? "",
    utmSource: getOrDefaultValue(utmParams?.utmSource as string),
    utmContent: getOrDefaultValue(utmParams?.utmContent as string),
    utmCampaign: getOrDefaultValue(utmParams?.utmCampaign as string),
    utmMedium: getOrDefaultValue(utmParams?.utmMedium as string),
    utmTerm: reqData?.utmTerm ?? "",
  };
};

/**
 * Removes M1-specific fields if not applicable
 * Original implementation from: lib/connectionManager/helper.ts
 * @param reqData - Request data
 * @returns Filtered request data
 */
export const removeM1Fields = (reqData: ISalplInsertApiRequest) => {
  if (reqData.reqSrc === "") {
    // if reqSrc not provided then remove m1 specific fields from standard insert call
    const { reqSrc, partnerBusiness, ...filteredReqData } = reqData;
    return filteredReqData;
  }
  return reqData;
};

/**
 * Sets UTM parameters for insert, with refer-and-earn decryption support
 * Original implementation from: lib/api/plInsertApi.ts setUtmParamsForInsert
 * @returns Object with UTM parameters (snake_case keys)
 */
const setUtmParamsForInsert = async () => {
  try {
    const campaignCookie = getCookie("campaignCookie")
      ? JSON.parse(getCookie("campaignCookie"))
      : {};
    const utmSource = getParams("utm_source") || campaignCookie.utm_source;
    const utmCampaign = getParams("utm_campaign") || campaignCookie.utm_campaign;
    let utmMedium = getParams("utm_medium") || campaignCookie.utm_medium;
    const utmTerm = getParams("utm_term") || campaignCookie.utm_term;
    if (utmSource?.toLowerCase() === "referandearn" && utmMedium) {
      const { REQ_BFL_REFER_KEY, REQ_BFL_REFER_IV } = await cryptoAPICalledData();
      if (REQ_BFL_REFER_KEY && REQ_BFL_REFER_IV) {
        utmMedium = await decryptAESCBC256(utmMedium, REQ_BFL_REFER_KEY, REQ_BFL_REFER_IV);
      }
    }
    return {
      utm_source: utmSource ?? "NA",
      utm_campaign: utmCampaign ?? "NA",
      utm_medium: utmMedium ?? "NA",
      utm_term: utmTerm ?? "NA",
    };
  } catch (error) {
    return {};
  }
};

/**
 * Sets UTM parameters for insert connection manager
 * Wraps setUtmParamsForInsert and maps snake_case keys to camelCase
 * Original implementation from: lib/connectionManager/helper.ts setUtmParamsForInsertConnectionManager
 * @returns Object with UTM parameters (camelCase keys)
 */
export const setUtmParamsForInsertConnectionManager = async () => {
  try {
    const utmParams = await setUtmParamsForInsert();
    return {
      utmSource: utmParams.utm_source ?? "NA",
      utmCampaign: utmParams.utm_campaign ?? "NA",
      utmMedium: utmParams.utm_medium ?? "NA",
      utmTerm: utmParams.utm_term ?? "NA",
    };
  } catch (error) {
    return {};
  }
};

/**
 * Detects if an error is a network error (no response received) vs a business error (response with statusCode)
 * Original implementation from: lib/connectionManager/helper.ts
 *
 * Network errors occur when:
 * - Fetch fails (e.g., "Failed to fetch", "NetworkError")
 * - Request times out
 * - Response is empty object {}
 * - Response is malformed/unparseable
 *
 * Business errors occur when:
 * - API returns a response with a statusCode field (e.g., 7003, 7005)
 *
 * IMPORTANT - ERROR THROWING CONTRACT:
 * This function relies on a specific error throwing pattern used across all insert APIs:
 * - Business errors: `throw new Error(JSON.stringify(response))` (error message contains JSON with statusCode)
 * - Network errors: `throw new Error("message")` (error message is plain string)
 *
 * @param error - The error object thrown by the API call
 * @param apiPath - The API path key (e.g., "salplInsert", "plcsInsert", "blInsert")
 */
export const detectAndRegisterNetworkError = (error: Error, apiPath: string): void => {
  const errorMessage = error.message;

  // Try to parse error message to check if it contains a statusCode
  let hasStatusCode = false;
  try {
    const errorObj = JSON.parse(errorMessage);
    hasStatusCode = errorObj.statusCode !== undefined;
  } catch {
    // Not a JSON error message, definitely a network/parsing error
    hasStatusCode = false;
  }

  // Only log network error if the error doesn't contain a statusCode
  if (!hasStatusCode) {
    logPageActionToNewRelic("NETWORK_ERROR", {
      component: `ConnectionManager_${apiPath}`,
      pageUrl: typeof window !== "undefined" ? window.location.href : "",
      message: errorMessage,
    });
  }
};
